import { useUserContext } from 'common/contexts/UserContext';
import ChipInput from 'components/ChipInput';
import * as React from 'react';
import { validateEmail } from 'services/helpers/user';

const MAX_EMAILS = 100;

interface Props {
  /** Callback fired on mount and whenever the entered emails change. Ony gets valid emails. */
  onEmailsChange: (emails: string[]) => void;
  error?: string;
}

export const AddYourTeamInput: React.FC<Props> = ({ onEmailsChange, error }) => {
  const { user } = useUserContext();
  const domain = user?.email?.split('@')[1];
  const displayDomain = domain ? `${domain}` : 'workeamil.com';
  const placeholderText = `e.g. john@${displayDomain}, maria@${displayDomain}`;

  const [inputRef, setInputRef] = React.useState<HTMLDivElement | undefined>();
  const [chips, setChips] = React.useState<string[]>([]);
  const [isActive, setIsActive] = React.useState<boolean>(false);
  const [isMaximumReached, setIsMaximumReached] = React.useState<boolean>(false);
  const chipInputRef = React.useRef<ChipInput>(null);

  const handleOnChange = (values: string[], emails: string[]) => {
    setChips(values);
    onEmailsChange(emails);
  };

  return (
    <div className="grow basis-1 space-y-2 flex flex-col text-dark">
      <div className="space-y-1 w-full ">
        <div
          className={`h-48 rounded-md p-4 overflow-y-scroll cursor-text border ${
            isActive ? 'border-blue-900' : 'border-gray-600'
          }`}
          onClick={() => inputRef?.focus()}
        >
          <ChipInput
            ref={chipInputRef}
            inputRef={(ref: HTMLDivElement) => setInputRef(ref)}
            classes={{
              inputContainer: 'w-full h-full flex flex-wrap content-start gap-y-2',
              inputRoot: 'w-full',
              input: 'p-0',
              copyButtonContainer: 'hidden',
              chip: {
                root: '',
                label: 'select-text',
              },
            }}
            InputProps={{
              'aria-label': 'add emails',
            }}
            defaultValue={[]}
            fullWidth={false}
            onMaximumReached={() => setIsMaximumReached(true)}
            maxNumberChips={MAX_EMAILS}
            alwaysShowPlaceholder
            placeholder={placeholderText}
            transformChipLabel={(label: string) => label.toLowerCase()}
            removeCharacters={['"', "'", ' ', '“', '”']}
            checkIfChipIsValid={(value: string) => validateEmail(value)}
            onChange={handleOnChange}
            onDelete={() => isMaximumReached && setIsMaximumReached(false)}
            onFocus={() => setIsActive(true)}
            onBlur={() => !chips.length && setIsActive(false)}
          />
        </div>
        <div className="flex w-full">
          {isMaximumReached && (
            <h4 className="text-red-900">
              You can&apos;t add more than {MAX_EMAILS} emails at once.
            </h4>
          )}
          {error && <h4 className="text-red-900">{error}</h4>}
        </div>
      </div>
    </div>
  );
};
